import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DesktopSidePanel, MadeWithLoveComponent } from "../../DesktopSidePanel";
import { MobileSidePanel } from "../../MobileSidePanel";
import {
  copyTextToClipboard,
  generateColorKey,
  generateId,
  generateImageUrls,
  getRandomMessage,
  handleCardClickAnimation,
} from "../../App.utils";
import { resetSpymasterStatus } from "../../playerUtils";
import { GameShell } from "../../core/components/GameShell";
import { buildGamePath } from "../../core/games/gamePaths";
import { useCurrentPlayer } from "../../core/hooks/useCurrentPlayer";
import { useWindowSize } from "../../core/hooks/useWindowSize";
import {
  mergeRoomState,
  setRoomState,
  subscribeToRoomState,
} from "../../core/state/roomStore";

const BOARD_SIZE = 20;
const DEFAULT_TIMER_SECONDS = 300;

function createEmptyBoard(fillValue) {
  return Array(BOARD_SIZE).fill(fillValue);
}

function Codenames({ gameId, gameDefinition }) {
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const pendingGameCreationRef = useRef(null);

  const [colors, setColors] = useState(createEmptyBoard(null));
  const [revealed, setRevealed] = useState(createEmptyBoard(false));
  const [isRedTurn, setIsRedTurn] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [gameMessage, setGameMessage] = useState("");
  const [isSpymaster, setIsSpymaster] = useState(false);
  const [recentMessages, setRecentMessages] = useState({});
  const [imageUrls, setImageUrls] = useState(createEmptyBoard(null));
  const [players, setPlayers] = useState({});
  const [timer, setTimer] = useState(DEFAULT_TIMER_SECONDS);
  const [defaultTimer, setDefaultTimer] = useState(DEFAULT_TIMER_SECONDS);
  const [clicked, setClicked] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [pausedAt, setPausedAt] = useState(null);
  const [totalPausedTime, setTotalPausedTime] = useState(0);
  const [isBoardRevealed, setIsBoardRevealed] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useCurrentPlayer();

  const updateGameState = useCallback(
    async (updates) => {
      if (!gameId) {
        return;
      }

      try {
        await mergeRoomState(gameId, updates);
      } catch (error) {
        console.error("Error updating game state:", error);
        if (error.code === "resource-exhausted") {
          setGameMessage("Too many updates, please wait a moment...");
        }
      }
    },
    [gameId],
  );

  const createNewGame = useCallback(async (allPlayers = {}) => {
    try {
      const newGameId = generateId();
      const isFirstTeamRed = Math.random() < 0.5;
      const colorKey = generateColorKey(isFirstTeamRed);
      const newImageUrls = generateImageUrls();

      const gameData = {
        colors: colorKey,
        revealed: createEmptyBoard(false),
        isRedTurn: isFirstTeamRed,
        gameOver: false,
        gameMessage: `A chilly new game begins! ${isFirstTeamRed ? "Red" : "Blue"} takes the first waddle`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        imageUrls: newImageUrls,
        players: resetSpymasterStatus(allPlayers),
      };

      await setRoomState(newGameId, gameData);
      return newGameId;
    } catch (error) {
      console.error("Error creating new game:", error);
      if (error.code === "resource-exhausted") {
        setGameMessage("Server is busy, please try again in a moment...");
        throw new Error("Server is busy");
      }
      throw error;
    }
  }, []);

  useEffect(() => {
    if (!gameId || !currentPlayer) {
      return;
    }

    if (
      JSON.stringify(players?.[currentPlayer.playerId]) ===
      JSON.stringify(currentPlayer)
    ) {
      return;
    }

    updateGameState({
      players: { ...players, [currentPlayer.playerId]: currentPlayer },
    });
  }, [currentPlayer, gameId, players, updateGameState]);

  useEffect(() => {
    if (!gameId) {
      return;
    }

    updateGameState({
      defaultTimer,
    });
  }, [defaultTimer, gameId, updateGameState]);

  useEffect(() => {
    setCurrentPlayer((previousPlayer) => {
      if (!previousPlayer || previousPlayer.isSpymaster === isSpymaster) {
        return previousPlayer;
      }

      return {
        ...previousPlayer,
        isSpymaster,
      };
    });
  }, [isSpymaster, setCurrentPlayer]);

  useEffect(() => {
    if (gameId || !currentPlayer) {
      return;
    }

    let isCancelled = false;

    if (!pendingGameCreationRef.current) {
      pendingGameCreationRef.current = createNewGame().finally(() => {
        pendingGameCreationRef.current = null;
      });
    }

    pendingGameCreationRef.current
      .then((newGameId) => {
        if (isCancelled) {
          return;
        }

        navigate(buildGamePath(gameDefinition.slug, newGameId), {
          replace: true,
        });
      })
      .catch((error) => {
        console.error("Error creating codenames game:", error);
      });

    return () => {
      isCancelled = true;
    };
  }, [createNewGame, currentPlayer, gameDefinition.slug, gameId, navigate]);

  useEffect(() => {
    if (!gameId) {
      return undefined;
    }

    const unsubscribe = subscribeToRoomState(gameId, (data) => {
      if (!data) {
        return;
      }

      if (data.redirectToGame) {
        navigate(buildGamePath(gameDefinition.slug, data.redirectToGame));
        return;
      }

      if (data.colors) {
        setColors(data.colors);
      }
      if (data.revealed) {
        setRevealed(data.revealed);
      }
      if ("isRedTurn" in data) {
        setIsRedTurn(data.isRedTurn);
      }
      if ("defaultTimer" in data) {
        setDefaultTimer(data.defaultTimer);
      }
      if ("updatedAt" in data) {
        setUpdatedAt(data.updatedAt);
      }
      if ("pausedAt" in data) {
        setPausedAt(data.pausedAt);
      }
      if ("totalPausedTime" in data) {
        setTotalPausedTime(data.totalPausedTime);
      }
      if ("gameOver" in data) {
        setGameOver(data.gameOver);
      }
      if (data.gameMessage) {
        setGameMessage(data.gameMessage);
      }
      if (data.imageUrls) {
        setImageUrls(data.imageUrls);
      }
      if (data.players) {
        setPlayers(data.players);
      }
    });

    return unsubscribe;
  }, [gameDefinition.slug, gameId, navigate]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    let interval = null;

    if (!gameOver && !pausedAt && timer > 0) {
      interval = setInterval(() => {
        setTimer((previousTimer) => {
          if (previousTimer <= 1) {
            setPausedAt(new Date().toISOString());
            return 0;
          }

          return previousTimer - 1;
        });
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [gameOver, pausedAt, timer]);

  useEffect(() => {
    if (!gameOver && updatedAt) {
      const now = new Date();
      const lastUpdate = new Date(updatedAt);
      const rawElapsed = Math.floor((now - lastUpdate) / 1000);
      const elapsed = rawElapsed - totalPausedTime;
      const remainingTime = Math.max(0, defaultTimer - elapsed);
      setTimer(remainingTime);
    }
  }, [defaultTimer, gameOver, pausedAt, totalPausedTime, updatedAt]);

  const handleClick = (index) => {
    if (!colors || revealed[index] || isSpymaster || gameOver) {
      return;
    }

    const newRevealed = [...revealed];
    newRevealed[index] = true;
    setRevealed(newRevealed);
    setClicked((previousClicked) => [...previousClicked, index]);

    if (colors[index] === "black") {
      const message = getRandomMessage(
        "assassin",
        recentMessages,
        setRecentMessages,
      );
      setGameOver(true);
      setGameMessage(message);
      updateGameState({
        revealed: newRevealed,
        gameOver: true,
        gameMessage: message,
      });
      handleCardClickAnimation(index, setClicked);
      return;
    }

    if (colors[index] === "neutral") {
      const nextIsRedTurn = !isRedTurn;
      const message = getRandomMessage(
        "neutral",
        recentMessages,
        setRecentMessages,
      );
      setIsRedTurn(nextIsRedTurn);
      setGameMessage(message);
      updateGameState({
        revealed: newRevealed,
        isRedTurn: nextIsRedTurn,
        gameMessage: message,
      });
      handleCardClickAnimation(index, setClicked);
      return;
    }

    const isCorrectTeam =
      (colors[index] === "red" && isRedTurn) ||
      (colors[index] === "blue" && !isRedTurn);
    const newIsRedTurn = isCorrectTeam ? isRedTurn : !isRedTurn;
    let message = gameOver
      ? gameMessage
      : isCorrectTeam
        ? getRandomMessage("correct", recentMessages, setRecentMessages)
        : getRandomMessage("wrong", recentMessages, setRecentMessages);

    const remainingRed = colors.filter(
      (color, colorIndex) => color === "red" && !newRevealed[colorIndex],
    ).length;
    const remainingBlue = colors.filter(
      (color, colorIndex) => color === "blue" && !newRevealed[colorIndex],
    ).length;

    let newGameOver = gameOver;
    if (!gameOver) {
      newGameOver = false;
      if (remainingRed === 0) {
        newGameOver = true;
        message = "Red wins! Mission accomplished!";
      } else if (remainingBlue === 0) {
        newGameOver = true;
        message = "Blue wins! Mission accomplished!";
      }
    }

    setIsRedTurn(newIsRedTurn);
    setGameOver(newGameOver);
    setGameMessage(message);
    updateGameState({
      revealed: newRevealed,
      isRedTurn: newIsRedTurn,
      gameOver: newGameOver,
      gameMessage: message,
    });

    handleCardClickAnimation(index, setClicked);
  };

  const handleTimerToggle = () => {
    const now = new Date();

    setPausedAt((previousPausedAt) => {
      let nextPausedAt;

      if (previousPausedAt === null) {
        nextPausedAt = now.toISOString();
        updateGameState({
          pausedAt: nextPausedAt,
          totalPausedTime: 0,
        });
      } else {
        const additionalPauseTime = previousPausedAt
          ? Math.floor((now - new Date(previousPausedAt)) / 1000)
          : 0;
        const newTotalPausedTime = totalPausedTime + additionalPauseTime;
        nextPausedAt = null;
        updateGameState({
          pausedAt: nextPausedAt,
          totalPausedTime: newTotalPausedTime,
        });
      }

      return nextPausedAt;
    });
  };

  const handleEndTurn = () => {
    if (gameOver) {
      return;
    }

    const newIsRedTurn = !isRedTurn;
    const newUpdatedAt = new Date().toISOString();

    setIsRedTurn(newIsRedTurn);
    setUpdatedAt(newUpdatedAt);
    updateGameState({
      colors,
      revealed,
      isRedTurn: newIsRedTurn,
      gameOver,
      updatedAt: newUpdatedAt,
      gameMessage: getRandomMessage(
        "endTurn",
        recentMessages,
        setRecentMessages,
      ),
    });
  };

  const handleReset = async () => {
    try {
      setGameMessage("Creating new game...");
      const refreshedPlayers = currentPlayer
        ? {
            ...players,
            [currentPlayer.playerId]: {
              ...currentPlayer,
              isSpymaster: false,
            },
          }
        : players;
      const newGameId = await createNewGame(refreshedPlayers);

      await updateGameState({
        redirectToGame: newGameId,
        redirectedAt: new Date().toISOString(),
        gameOver: true,
        gameMessage: "Starting new game...",
      });

      setTimer(defaultTimer);
      setPausedAt(null);
      setClicked([]);
      setIsSpymaster(false);
      setGameOver(false);
      setIsBoardRevealed(false);
      setCurrentPlayer((previousPlayer) =>
        previousPlayer
          ? {
              ...previousPlayer,
              isSpymaster: false,
            }
          : previousPlayer,
      );
      navigate(buildGamePath(gameDefinition.slug, newGameId));
    } catch (error) {
      console.error("Error resetting game:", error);
      if (error.message === "Server is busy") {
        return;
      }
      setGameMessage("Failed to create new game. Please try again.");
    }
  };

  const handleShare = async () => {
    const copied = await copyTextToClipboard(window.location.href);
    setGameMessage(
      copied
        ? "Game link copied to clipboard!"
        : "Couldn't copy the game link on this browser.",
    );
    setTimeout(() => {
      setGameMessage("");
    }, 2000);
  };

  const board = (
    <div className="grid-container">
      {colors.map((color, index) => (
        <div
          key={index}
          className={`grid-item ${isBoardRevealed ? "" : clicked.includes(index) ? "just-clicked" : revealed[index] ? "revealed" : ""} ${isBoardRevealed || isSpymaster ? `spymaster-${color}` : ""}`}
          onClick={() => {
            if (window.matchMedia("(min-width: 768px)").matches) {
              handleClick(index);
            }
          }}
          onTouchStart={(event) => {
            const now = Date.now();
            const lastTap =
              event.currentTarget.getAttribute("data-lasttap") || 0;
            const tapLength = now - lastTap;

            document.querySelectorAll(".grid-item").forEach((item) => {
              item.classList.remove("touch-hover");
            });

            if (tapLength < 300 && tapLength > 0) {
              handleClick(index);
            } else {
              event.currentTarget.classList.add("touch-hover");
            }

            event.currentTarget.setAttribute("data-lasttap", now);
          }}
          onTouchMove={() => {
            document.querySelectorAll(".grid-item").forEach((item) => {
              item.classList.remove("touch-hover");
            });
          }}
          onTouchEnd={() => {
            setTimeout(() => {
              document.querySelectorAll(".grid-item").forEach((item) => {
                item.classList.remove("touch-hover");
              });
            }, 500);
          }}
        >
          <div className="card-inner">
            <div className="card-face front">
              <img
                src={imageUrls[index]}
                alt={`Card ${index}`}
                className="card-image"
              />
            </div>
            <div className="card-face back">
              <img
                src={`/${color}_2.png`}
                alt={`${color} card`}
                className="card-image"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const sharedPanelProps = {
    isRedTurn,
    handleShare,
    colors,
    revealed,
    isSpymaster,
    gameOver,
    timer,
    formatTime,
    defaultTimer,
    setDefaultTimer,
    setTimer,
    handleEndTurn,
    gameMessage,
    setIsSpymaster,
    players,
    handleReset,
    windowSize: width,
    isBoardRevealed,
    setIsBoardRevealed,
    handleTimerToggle,
    pausedAt,
  };

  if (!gameId) {
    return (
      <div className="App">
        <div className="game-loading">
          Setting up a chilly new room...
        </div>
      </div>
    );
  }

  return (
    <GameShell
      width={width}
      board={board}
      desktopSidebar={<DesktopSidePanel {...sharedPanelProps} />}
      mobileSidebar={<MobileSidePanel {...sharedPanelProps} />}
      footer={<MadeWithLoveComponent />}
    />
  );
}

export default Codenames;
